import type { ButtonHTMLAttributes } from 'react'
import type { ActionId } from '@/annotations'

/**
 * `button.btn` / `button.btn.full` / `button.btn.ghost` → `button` (process/08-COMPONENT-
 * CATALOGUE.md). border-radius 3px → --r-1; gap 6px → --sp-3 (0 delta). `.ghost`: background
 * `--panel`, border `--bd2` (width not a token family).
 *
 * Sizing is fixed-height, not padding-driven (W186, `design/33-client-conventions.md` §33.9 "Button
 * geometry") — Atlassian Design System's own button spec (32px default / 24px compact, 12px/8px
 * horizontal padding, medium weight), not the prototype's padding-driven `.btn` rule. Reported live
 * as "bigger than modern SaaS reference" against the prototype-derived padding+bold version; ADS is
 * the relevant reference (not Material) because this app's colour tokens are already lifted from it
 * (`--blue: #0052cc` is ADS blue, `--r-1: 3px` already matches ADS's button radius exactly). Height
 * is out of the token map's scope either way (same class of gap `.full`'s own height already
 * documented), so a hard px height here isn't a new kind of exception.
 *
 * `full` (width 100%) is opt-in, not this component's default sizing — a single-CTA button below a
 * form sizes to its own label unless a caller has a real reason (uniform width across a set of
 * variable-length labels; `a-orgblocked`'s org-switch list is the one caller left) to ask for full
 * width explicitly.
 *
 * The prop is spelled `data-act`, not `dataAct` — deliberately, so `eslint-rules/data-act-valid`
 * sees the same literal at every call site (`<Button data-act="session.confirm">`) that it would
 * see on a plain `<button>`, rather than being blind to it one prop-name hop away.
 *
 * `danger` (W144, `_family-modals.md` M1: "a destructive commit is the `danger` variant") —
 * `.btn.danger` in Prototype_v2.html: background `#c9372c`, hover `#ae2e24`. Both fold to
 * `--accent-danger` in process/11-TOKEN-MAP.md (lines 131–140, δ1 each) — one token, not two.
 * Weight is semibold, not bold, for the same slimming reason as the sizing above — bold read as
 * heavier than any of `danger`/primary/`ghost` need to look next to each other.
 *
 * `size="sm"` — 39 real prototype usages (`.btn.sm` 4, `.btn.ghost.sm` 32, `.btn.danger.sm` 3)
 * with no way to express any of them here until this prop existed; every call site this component
 * ever had used the single default size regardless of context. `Pager.tsx` (found live: full-size
 * ghost buttons at the foot of every table read as oversized) is the first real caller, not the
 * only intended one — a compact secondary/tertiary action next to denser content is exactly what
 * the prototype's own `.sm` rows are for.
 */
export function Button({
  'data-act': dataAct,
  full,
  ghost,
  danger,
  size = 'default',
  children,
  ...rest
}: {
  'data-act': ActionId | undefined
  full?: boolean
  ghost?: boolean
  danger?: boolean
  size?: 'default' | 'sm'
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const base = danger
    ? 'text-white bg-accent-danger hover:brightness-90 font-semibold disabled:bg-slate'
    : ghost
      ? 'bg-panel text-tx border border-bd2 font-semibold hover:bg-row disabled:bg-row disabled:text-muted disabled:border-bd'
      : 'text-white bg-blue hover:bg-blue-hover font-semibold disabled:bg-slate'
  const fontSize = size === 'sm' ? 'text-body' : 'text-body-lg'
  const horizontalPadding = size === 'sm' ? 'px-4' : 'px-6'
  const height = size === 'sm' ? '24px' : '32px'
  return (
    <button
      data-cmp="button"
      data-act={dataAct}
      style={{ height, ...(full ? { width: '100%' } : {}) }}
      className={`inline-flex items-center justify-center gap-3 rounded-1 disabled:cursor-not-allowed ${fontSize} ${horizontalPadding} ${base}`}
      {...rest}
    >
      {children}
    </button>
  )
}
